import React, { useEffect, useRef, useState } from 'react';
import { Badge, Dropdown, List, Button, Empty, Tag } from 'antd';
import { BellOutlined, CheckOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth.store';
import { apiClient } from '@/api/client';

interface NotificationEvent {
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  link?: string;
  timestamp: string;
}

const typeColor: Record<NotificationEvent['type'], string> = {
  info: 'blue',
  success: 'green',
  warning: 'orange',
  error: 'red',
};

function timeAgo(ts: string): string {
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60) return `${diff}秒前`;
  if (diff < 3600) return `${Math.floor(diff / 60)}分钟前`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}小时前`;
  return new Date(ts).toLocaleDateString('zh-CN');
}

export const NotificationBell: React.FC = () => {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const [live, setLive] = useState<NotificationEvent[]>([]);
  const esRef = useRef<EventSource | null>(null);
  const [open, setOpen] = useState(false);

  // Fetch recent buffered notifications on mount
  const { data: recentData } = useQuery({
    queryKey: ['notifications-recent'],
    queryFn: () => apiClient.get<{ data: NotificationEvent[] }>('/api/v1/notifications/recent'),
    staleTime: 0,
  });
  const buffered: NotificationEvent[] = recentData?.data?.data ?? [];

  // All notifications = live + buffered (deduplicated by timestamp)
  const allTs = new Set(live.map((n) => n.timestamp));
  const combined = [...live, ...buffered.filter((n) => !allTs.has(n.timestamp))].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );

  // Connect SSE
  useEffect(() => {
    if (!token) return;

    const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
    const url = `${apiBase}/api/v1/notifications/stream?token=${encodeURIComponent(token)}`;

    const es = new EventSource(url);
    esRef.current = es;

    es.onmessage = (e: MessageEvent<string>) => {
      if (!e.data || e.data.trim() === '') return; // keepalive
      try {
        const ev = JSON.parse(e.data) as NotificationEvent;
        setLive((prev) => [ev, ...prev].slice(0, 50));
        void queryClient.invalidateQueries({ queryKey: ['notifications-recent'] });
      } catch {
        // ignore malformed events
      }
    };

    es.onerror = () => {
      // Browser auto-reconnects; no action needed
    };

    return () => {
      es.close();
      esRef.current = null;
    };
  }, [token, queryClient]);

  const handleClearAll = async () => {
    await apiClient.delete('/api/v1/notifications/clear');
    setLive([]);
    void queryClient.invalidateQueries({ queryKey: ['notifications-recent'] });
  };

  const unreadCount = combined.length;

  const dropdownContent = (
    <div style={{ width: 360, maxHeight: 480, overflowY: 'auto', background: '#fff', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
        <span style={{ fontWeight: 600 }}>通知</span>
        {unreadCount > 0 && (
          <Button type="link" size="small" icon={<CheckOutlined />} onClick={handleClearAll}>
            全部已读
          </Button>
        )}
      </div>
      {combined.length === 0 ? (
        <Empty description="暂无通知" style={{ padding: '32px 0' }} />
      ) : (
        <List
          dataSource={combined}
          renderItem={(item) => (
            <List.Item
              style={{ padding: '10px 16px', cursor: item.link ? 'pointer' : 'default' }}
              onClick={() => { if (item.link) window.location.hash = item.link; }}
            >
              <List.Item.Meta
                title={
                  <span>
                    <Tag color={typeColor[item.type]} style={{ marginRight: 6 }}>
                      {item.type === 'info' ? '提示' : item.type === 'success' ? '成功' : item.type === 'warning' ? '警告' : '错误'}
                    </Tag>
                    {item.title}
                  </span>
                }
                description={
                  <span>
                    <span style={{ fontSize: 12 }}>{item.message}</span>
                    <span style={{ float: 'right', fontSize: 11, color: '#999' }}>{timeAgo(item.timestamp)}</span>
                  </span>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );

  return (
    <Dropdown
      dropdownRender={() => dropdownContent}
      trigger={['click']}
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
    >
      <Badge count={unreadCount} size="small" style={{ cursor: 'pointer' }}>
        <BellOutlined style={{ fontSize: 18, color: '#555', cursor: 'pointer' }} />
      </Badge>
    </Dropdown>
  );
};
